import React, { useState, MouseEvent, useEffect } from "react"
import { Button, Menu, MenuItem, ListItemText, Box } from "@mui/material"

// Define interfaces for menu items
interface MenuItemData {
  value: string
  label: string
  items?: MenuItemData[] // For submenu items
}

interface DropdownMenuProps {
  placeholder?: string
  items: MenuItemData[]
  onChange?: (value: string, label: string) => void // Changed to single strings
  value?: string[] // Kept as array for internal state
  multiple?: boolean
  type?: string
}

const DropdownMenu: React.FC<DropdownMenuProps> = React.memo(
  ({
    placeholder = "Select an option",
    items = [],
    onChange,
    value = [], // Default to empty array
    multiple = true,
    type = "",
  }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [submenuAnchorEl, setSubmenuAnchorEl] = useState<null | HTMLElement>(null)
    const [currentSubmenu, setCurrentSubmenu] = useState<MenuItemData | null>(null)

    // Track selected values and labels separately
    const [selectedValues, setSelectedValues] = useState<string[]>(Array.isArray(value) ? value : [])
    const [selectedLabels, setSelectedLabels] = useState<string[]>([])

    // Update selected labels when value changes externally
    useEffect(() => {
      const valueArray = Array.isArray(value) ? value : []
      setSelectedValues(valueArray)

      if (valueArray.length > 0) {
        // Find labels for all selected values
        const findLabels = (items: MenuItemData[]): string[] => {
          let result: string[] = []

          for (const item of items) {
            if (valueArray.includes(item.value)) {
              result.push(item.label)
            }

            if (item.items) {
              const subLabels = findLabels(item.items)
              result = [...result, ...subLabels]
            }
          }

          return result
        }

        const labels = findLabels(items)
        setSelectedLabels(labels)
      } else {
        setSelectedLabels([])
      }
    }, [value, items])

    const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget)
    }

    const handleCloseMenu = () => {
      setAnchorEl(null)
      setSubmenuAnchorEl(null)
      setCurrentSubmenu(null)
    }

    const handleOpenSubmenu = (event: MouseEvent<HTMLElement>, item: MenuItemData) => {
      setCurrentSubmenu(item)
      setSubmenuAnchorEl(event.currentTarget)
      // Stop propagation to prevent main menu from closing
      event.stopPropagation()
    }

    const handleCloseSubmenu = () => {
      setSubmenuAnchorEl(null)
      setCurrentSubmenu(null)
    }

    const handleSelectItem = (item: MenuItemData, event?: React.MouseEvent) => {
      // Prevent closing the menu for multiple selection
      if (event && multiple) {
        event.stopPropagation()
      }

      let newValues: string[] = []
      let newLabels: string[] = []

      if (multiple) {
        // Toggle selection
        if (selectedValues.includes(item.value)) {
          newValues = selectedValues.filter((v) => v !== item.value)
          newLabels = selectedLabels.filter((l) => l !== item.label)
        } else {
          newValues = [...selectedValues, item.value]
          newLabels = [...selectedLabels, item.label]
        }

        setSelectedValues(newValues)
        setSelectedLabels(newLabels)

        if (onChange) {
          // Convert arrays to comma-separated strings
          onChange(newValues.join(","), newLabels.join(","))
        }
      } else {
        // Single selection
        setSelectedValues([item.value])
        setSelectedLabels([item.label])
        handleCloseMenu()

        if (onChange) {
          // For single selection, no need to join but keep consistent
          onChange(item.value, item.label)
        }
      }
    }

    // Remove a selected item
    const handleRemoveItem = (value: string, label: string, event: React.MouseEvent) => {
      event.stopPropagation() // Prevent opening the dropdown

      const newValues = selectedValues.filter((v) => v !== value)
      const newLabels = selectedLabels.filter((l) => l !== label)

      setSelectedValues(newValues)
      setSelectedLabels(newLabels)

      if (onChange) {
        // Convert arrays to comma-separated strings
        onChange(newValues.join(","), newLabels.join(","))
      }
    }

    // Display selected items or placeholder
    const renderButtonContent = () => {
      if (selectedLabels.length === 0) {
        return placeholder
      }

      // If only one item is selected, show its label
      if (selectedLabels.length === 1) {
        return selectedLabels[0]
      }

      // If multiple items are selected, show count with type if provided
      if (type) {
        return `${selectedLabels.length} ${type} selected`
      } else {
        return `${selectedLabels.length} selected`
      }
    }

    return (
      <>
        <Button
          variant="outlined"
          onClick={handleOpenMenu}
          fullWidth
          sx={{
            backgroundColor: "#ffffff",
            color: "#000000",
            border: "1px solid #C4C4C4",
            borderRadius: "5px",
            textTransform: "none",
            padding: "8px 16px",
            justifyContent: "flex-start",
            textAlign: "left",
            minHeight: "38px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {renderButtonContent()}
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          sx={{
            "& .MuiPaper-root": {
              borderRadius: "4px",
            },
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            style: {
              width: anchorEl?.offsetWidth,
            },
          }}
        >
          {items.map((item) =>
            item.items && item.items.length > 0 ? (
              <MenuItem
                key={item.value}
                onClick={(e) => handleOpenSubmenu(e, item)}
                sx={{
                  backgroundColor: selectedValues.includes(item.value) ? "#e3f2fd" : "inherit", // Light blue background when selected
                  "&:hover": {
                    backgroundColor: selectedValues.includes(item.value) ? "#bbdefb" : "rgba(0, 0, 0, 0.04)", // Darker blue on hover when selected
                  },
                }}
              >
                <ListItemText primary={item.label} />
                <span style={{ marginLeft: "8px" }}>›</span>
              </MenuItem>
            ) : (
              <MenuItem
                key={item.value}
                onClick={(e) => handleSelectItem(item, e)}
                sx={{
                  justifyContent: multiple ? "flex-start" : "space-between",
                  backgroundColor: selectedValues.includes(item.value) ? "#e3f2fd" : "inherit", // Light blue background when selected
                  "&:hover": {
                    backgroundColor: selectedValues.includes(item.value) ? "#bbdefb" : "rgba(0, 0, 0, 0.04)", // Darker blue on hover when selected
                  },
                }}
              >
                <ListItemText primary={item.label} />
              </MenuItem>
            ),
          )}
          {multiple && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
              <Button size="small" onClick={handleCloseMenu} variant="contained" sx={{ marginLeft: "auto" }}>
                Done
              </Button>
            </Box>
          )}
        </Menu>

        {/* Submenu */}
        {currentSubmenu && currentSubmenu.items && (
          <Menu
            anchorEl={submenuAnchorEl}
            open={Boolean(submenuAnchorEl)}
            onClose={handleCloseSubmenu}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              style: {
                width: anchorEl?.offsetWidth,
              },
            }}
          >
            {currentSubmenu.items.map((subItem) => (
              <MenuItem
                key={subItem.value}
                onClick={(e) => handleSelectItem(subItem, e)}
                sx={{
                  justifyContent: multiple ? "flex-start" : "space-between",
                  backgroundColor: selectedValues.includes(subItem.value) ? "#e3f2fd" : "inherit", // Light blue background when selected
                  "&:hover": {
                    backgroundColor: selectedValues.includes(subItem.value) ? "#bbdefb" : "rgba(0, 0, 0, 0.04)", // Darker blue on hover when selected
                  },
                }}
              >
                <ListItemText primary={subItem.label} />
              </MenuItem>
            ))}
            {multiple && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
                <Button size="small" onClick={handleCloseSubmenu} variant="contained" sx={{ marginLeft: "auto" }}>
                  Done
                </Button>
              </Box>
            )}
          </Menu>
        )}
      </>
    )
  },
  (prevProps, nextProps) => {
    // Custom comparison - only re-render if these props actually change
    return (
      prevProps.placeholder === nextProps.placeholder &&
      prevProps.multiple === nextProps.multiple &&
      prevProps.type === nextProps.type &&
      JSON.stringify(prevProps.value) === JSON.stringify(nextProps.value) &&
      JSON.stringify(prevProps.items) === JSON.stringify(nextProps.items)
      // Don't compare onChange function reference
    )
  },
)

DropdownMenu.displayName = "DropdownMenu"

export default DropdownMenu
